import { useEffect, useState } from 'react';
import styled, { useTheme, css } from 'styled-components';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';

import Brand from '@/components/brand';
import SEO from '@/components/seo';
import HowItWorks from '@/components/how-it-works';
import OtherBrandsInCountry from '@/components/other-brands-in-country';
import AppBanner from '@/components/app-banner';
import BrandStore from '@/stores/brand-store';
import { useStore } from '@/stores/store-provider';
import { shareImageForBrand, parseIPAddress, getAllBrandsAndTopupsFromResults } from '@/utils';
// import enabledBrands from '@data/enabled-brands.json';
import { fetchBrandNew, searchBrands } from '@/api';
import { media } from '@/themes';
import config from '@/config';
import Bugsnag from '@/bugsnag';
import { trackViewedBrandDetails } from '../../tracker';
import EarlyExit from '@/components/early-exit';
import NewNetworksBanner from '@/components/new-networks-banner';

const SearchField = dynamic(() => import('@/components/search-field'), { ssr: false });
const Toolbar = dynamic(() => import('@/components/toolbar'), { ssr: false });
// const AppBanner = dynamic(() => import('@/components/app-banner'), { ssr: false });

const Container = styled.div`
  margin: 0 auto;
  max-width: ${props => props.theme.maxContentWidth};
  padding: 0 1em 3em;

  ${media.sm`
    padding-bottom: 0;
  `};
`;

const MaintenanceBanner = styled.div`
  background: #ffdddd;
  padding: 1em;
  font-weight: 300;
  font-size: 0.9em;
  text-align: center;
`;

const Search = styled(SearchField)`
  background: rgba(0, 0, 0, .025);
  border: 1px solid transparent;
  border-radius: .25em;
  height: 100%;
  margin: auto;
  max-width: 30em;

  ${media.sm`
    display: none !important;
  `};

  &.search-field--focused {
    background: white;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 .15em ${props => props.theme.colors.primary}66;
  }
`;

const BrandDetails = ({ initialBrand, currencies, router }) => {
  const { data: brand, error } = useSWR([initialBrand.country.slug, initialBrand.slug], fetchBrandNew, { fallbackData: initialBrand, revalidateOnMount: true });
  error && console.log(`error`, error);
  const theme = useTheme();
  const cart = useStore();
  const [store] = useState(() => new BrandStore(brand, cart, null));
  const t = useTranslations('brands');
  const shareImageUrl = shareImageForBrand(brand);
  const url = `${config.rootUrl}${router.asPath}`;
  const { type } = brand;
  const seoText = t('seoText', { type, brandName: brand.name });
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [cachedSuggestions, setCachedSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const seoTitle = `${seoText} ${currencies.map(cur => cur.displayName).join(', ')}`;
  const keywords = `${brand.name} giftcard giftcards vouchers topups topup refill refills ${currencies.map(cur => cur.displayName).join(' ')} ${currencies.map(cur => cur.displaySymbol).join(' ')}`;
  const seoDescription = `${seoText} Bitcoin (BTC), USDC, USDT (Tether), XLM (Stellar), Ethereum, Litecoin, Dai, Polygon (MATIC), Fantom (FTM), Avalanche (AVAX), Solana (SOL), Algorand (ALGO) ${t('manyOtherCoins')}`;

  const handleBlur = () => {
    setCachedSuggestions(suggestions);
    setTimeout(() => setSuggestions([]), 250);
  };

  const handleFocus = () => {
    if (cachedSuggestions.length > 0) {
      setSuggestions(cachedSuggestions);
    }
  };

  const handleSearchTerm = event => {
    setSearchTerm(event.target.value);
  };

  const handleOnChange = async event => {
    const { value } = event.target;
    setSuggestions([]);

    if (value.trim()) {
      setIsLoadingSuggestions(true);
      const { results } = await searchBrands({ $search: value.trim() });
      const suggestions = getAllBrandsAndTopupsFromResults(results);

      setSuggestions(suggestions.sort(brand => {
        if (brand.name.toLowerCase().startsWith(value.toLowerCase())) {
          return -1;
        } else {
          return 0;
        }
      }));
      setIsLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    store.setProduct(null);
    store.setAmount(0);
  }, [router.asPath, store]);

  useEffect(() => {
    store.setBrand(brand);
  }, [brand, store]);

  useEffect(() => {
    trackViewedBrandDetails(initialBrand);
  }, [initialBrand]);

  // TODO: Add custom SEO description for each brand? Make it optional...
  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={keywords}
        url={url}
        imageUrl={shareImageUrl}
      />
      {config.inMaintenanceMode && <MaintenanceBanner>Ordering is currently disabled while we are doing some maintenance and upgrades to our system.<br />We&apos;ll be back up shortly!</MaintenanceBanner>}
      <NewNetworksBanner />
      <Container>
        <Toolbar>
          <Search
            isLoadingSuggestions={isLoadingSuggestions}
            onBlur={handleBlur}
            onChange={handleOnChange}
            onFocus={handleFocus}
            search={() => { }}
            searchTerm={searchTerm}
            setSearchTerm={handleSearchTerm}
            suggestions={suggestions} />
        </Toolbar>
        {brand &&
          <Brand
            key={brand.id}
            brand={brand}
            seoText={seoDescription}
            store={store}
            products={brand.products}
            router={router} />
        }
      </Container>
      {brand && <OtherBrandsInCountry brand={brand} />}
      <AppBanner background={theme.colors.primary} inverted source="brand_details" />
      {brand && <HowItWorks type={brand.type} />}
    </>
  )
};

const Index = ({ initialBrand, currencies }) => {
  const router = useRouter();

  return (
    <EarlyExit
      Component={BrandDetails}
      condition={router.isFallback}
      currencies={currencies}
      initialBrand={initialBrand} />
  );
};

export async function getServerSideProps ({ params, req, locale }) {

  if (req.headers['x-bb-ip'] === '92.0.84.169') {
    return {
      redirect: {
        permanent: false,
        destination: 'https://google.com',
      }
    };
  }
  //TODO: Error handling and log to Bugsnag
  const countrySlug = params.slug[0];
  const slug = params.slug[1];
  // let brand = enabledBrands.find(b => b.country.slug === countrySlug && b.slug === slug);
  let brand = await fetchBrandNew([countrySlug, slug]);
  // if (!brand) {
  // }
  console.log('getServerSideProps', countrySlug, slug, brand?.id);
  if (!brand) {
    Bugsnag.notify(new Error(`Brand not found for ${countrySlug}/${slug}`), event => {
      event.addMetadata('headers', req.headers);
      event.addMetadata('other', { ipAddress: parseIPAddress(req) });
      return req.headers.from !== 'tech@babbar.tech';
    });
    return { notFound: true };
  }
  delete brand.products;

  return {
    props: {
      initialBrand: brand,
      messages: require(`@/locales/${locale}.json`)
    }
  };
}

export default Index;
